<template>
  <div class="charte">
    <h4><q-icon name="palette" /> Charte graphique</h4>
    <p class="charte-hint">Définie une fois, appliquée par défaut à l'accueil et aux autres pages. Les réglages fins (page d'un point) restent prioritaires.</p>

    <div class="charte-fields">
      <!-- Réglages -->
      <div>
        <div class="section-label">Titre</div>
        <q-select dense outlined v-model="style.title_font" :options="fontOptions" label="Police" />
        <div class="row q-gutter-sm q-mt-sm">
          <q-input dense outlined type="number" v-model.number="style.title_size" label="Taille (px)" class="col" />
          <q-select dense outlined v-model="style.title_weight" :options="weightOptions" label="Graisse" emit-value map-options class="col" />
        </div>
        <q-input dense outlined v-model="style.title_color" label="Couleur" class="q-mt-sm">
          <template v-slot:append>
            <q-icon name="colorize" class="cursor-pointer"><q-popup-proxy cover><q-color v-model="style.title_color" /></q-popup-proxy></q-icon>
          </template>
        </q-input>

        <div class="section-label q-mt-md">Texte courant</div>
        <q-select dense outlined v-model="style.text_font" :options="fontOptions" label="Police" />
        <q-input dense outlined type="number" v-model.number="style.text_size" label="Taille (px)" class="q-mt-sm" />
        <q-input dense outlined v-model="style.text_color" label="Couleur" class="q-mt-sm">
          <template v-slot:append>
            <q-icon name="colorize" class="cursor-pointer"><q-popup-proxy cover><q-color v-model="style.text_color" /></q-popup-proxy></q-icon>
          </template>
        </q-input>

        <div class="section-label q-mt-md">Fond</div>
        <q-input dense outlined v-model="style.bg_color" label="Couleur de fond">
          <template v-slot:append>
            <q-icon name="colorize" class="cursor-pointer"><q-popup-proxy cover><q-color v-model="style.bg_color" /></q-popup-proxy></q-icon>
          </template>
        </q-input>

        <div class="section-label q-mt-md">Palette réutilisable</div>
        <div class="row items-center q-gutter-xs">
          <div v-for="(c, i) in style.palette" :key="i" class="swatch" :style="{ background: c }" @click="removeColor(i)">
            <q-tooltip>Cliquer pour retirer</q-tooltip>
          </div>
          <q-btn round dense flat icon="add" color="secondary">
            <q-popup-proxy cover><q-color v-model="newColor" @update:model-value="addColor" /></q-popup-proxy>
          </q-btn>
        </div>

        <div class="q-mt-md">
          <q-btn color="secondary" label="Enregistrer la charte" @click="onSave" :loading="saving" />
          <span v-if="saved" class="text-positive q-ml-sm">✓ enregistrée</span>
          <span v-if="saveError" class="text-negative q-ml-sm">✗ erreur</span>
        </div>
      </div>

      <!-- Aperçu -->
      <div class="q-mt-lg">
        <div class="section-label">Aperçu</div>
        <div class="preview" :style="{ background: style.bg_color }">
          <div :style="{ fontFamily: style.title_font, fontSize: style.title_size + 'px', color: style.title_color, fontWeight: style.title_weight, lineHeight: 1.1 }">
            Titre d'exemple
          </div>
          <p :style="{ fontFamily: style.text_font, fontSize: style.text_size + 'px', color: style.text_color }">
            Ceci est un paragraphe d'exemple pour visualiser le style du texte courant de votre exposition.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps } from 'vue';
import { useExhibitStyle } from '../composables/useExhibitStyle';

const props = defineProps({ exhibitId: { required: true, default: null } });
const { style, load, save } = useExhibitStyle(props.exhibitId);

const fontOptions = ['Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New', 'Trebuchet MS', 'Tahoma', 'Garamond', 'Palatino'];
const weightOptions = [
  { label: 'Normal', value: '400' },
  { label: 'Semi-gras', value: '600' },
  { label: 'Gras', value: '700' },
  { label: 'Extra-gras', value: '800' },
];

const newColor = ref('#8257d4');
const saving = ref(false);
const saved = ref(false);
const saveError = ref(false);

const addColor = (val) => {
  if (val && !style.value.palette.includes(val)) style.value.palette.push(val);
};
const removeColor = (i) => style.value.palette.splice(i, 1);

const onSave = async () => {
  saving.value = true;
  saveError.value = false;
  try {
    await save();
    saved.value = true;
    setTimeout(() => { saved.value = false; }, 2000);
  } catch (e) {
    console.error(e);
    saveError.value = true;
    setTimeout(() => { saveError.value = false; }, 2500);
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<style scoped>
.charte { margin-top: 1rem; }
.charte-hint { font-size: .8rem; color: #888; margin: 0 0 .5rem; }
.section-label { font-size: .72rem; text-transform: uppercase; letter-spacing: .05em; color: #757575; font-weight: 700; margin-bottom: .35rem; }
.swatch { width: 28px; height: 28px; border-radius: 6px; border: 1px solid #ddd; cursor: pointer; }
.preview { border: 1px solid #e0e0e0; border-radius: 10px; padding: 1.25rem; min-height: 140px; overflow: hidden; box-sizing: border-box; }
.preview p { margin-top: .6rem; line-height: 1.5; }
.preview > div, .preview p { overflow-wrap: anywhere; word-break: break-word; max-width: 100%; }
h4 {
    font-size: 20px;
    font-weight: 600;
}
</style>
